import { ArrowLeft, CalendarClock, Image as ImageIcon, Inbox, Layers, Loader2, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import DynamicFormRenderer from '../../components/DynamicFormRenderer';
import { useLang } from '../../LangContext';

// Kullanıcı form doldurmadan önce sabit bir adım olarak kalıp seçer; seçilen kalıba göre
// kendi diline uygun form (yoksa Türkçe, o da yoksa kalıba bağlı ilk dil) otomatik yüklenir.
export default function UserFormsPage({ moldTypes, submitForm, loadFormDetail }) {
  // Kalıplardan hangisi seçili — birden fazla varsa kullanıcı seçer
  const [selectedMoldId, setSelectedMoldId] = useState(null);
  const [fullForm, setFullForm] = useState(null);
  const [loadingForm, setLoadingForm] = useState(false);
  const { locale: t, lang } = useLang();

  // Kalıp sayısı değişirse seçimi yenile:
  // - 1 kalıp varsa otomatik seç
  // - Birden fazla varsa kullanıcı seçsin diye temizle
  // - Seçili kalıp artık listede yoksa temizle
  useEffect(() => {
    if (!moldTypes || moldTypes.length === 0) {
      setSelectedMoldId(null);
      return;
    }
    if (moldTypes.length === 1) {
      setSelectedMoldId(moldTypes[0].id);
      return;
    }
    if (selectedMoldId && !moldTypes.some((mold) => mold.id === selectedMoldId)) {
      setSelectedMoldId(null);
    }
  }, [moldTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seçili kalıbın slim referansı (picker kartı bilgisi)
  const slimMold = useMemo(
    () => moldTypes?.find((mold) => mold.id === selectedMoldId) || null,
    [moldTypes, selectedMoldId],
  );

  // Seçili kalıp değişince, kullanıcının diline uygun tam form içeriğini çek
  useEffect(() => {
    if (!slimMold?.id) { setFullForm(null); return; }
    let cancelled = false;
    setLoadingForm(true);
    setFullForm(null);
    loadFormDetail(slimMold.id).then((data) => {
      if (!cancelled) { setFullForm(data); setLoadingForm(false); }
    });
    return () => { cancelled = true; };
  }, [slimMold?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Gönderim sonrası: birden fazla kalıp varsa seçim ekranına dönüş için sarmalayıcı
  async function handleSubmit(formId, data) {
    const ok = await submitForm(formId, data);
    if (ok && moldTypes.length > 1) {
      // Çoklu kalıpta gönderim sonrası listeye geri dön — kullanıcı diğer kalıpları görebilsin
      setSelectedMoldId(null);
    }
    return ok;
  }

  // Bugünün tarihi — kurumsal üst şeritte tarih bilgisi
  const today = useMemo(() => new Date().toLocaleDateString(`${lang}-${lang.toUpperCase()}`, {
    day: '2-digit', month: 'long', year: 'numeric',
  }), [lang]);

  // Hiç kalıp yok — kurumsal stilde boş ekran
  if (!moldTypes || moldTypes.length === 0) {
    return (
      <section className="uf-stage uf-stage-empty">
        <div className="uf-empty-card">
          <span className="uf-empty-icon"><Inbox size={32} /></span>
          <h2>{t.userForms.noMoldTitle}</h2>
          <p>{t.userForms.noMoldDesc}</p>
          <div className="uf-empty-meta">
            <span><CalendarClock size={14} /> {today}</span>
            <span><ShieldCheck size={14} /> {t.userForms.secureConnection}</span>
          </div>
        </div>
      </section>
    );
  }

  // Birden fazla kalıp var ve henüz seçim yapılmadı — kalıp seçim ekranını göster
  if (moldTypes.length > 1 && !selectedMoldId) {
    return (
      <section className="uf-stage">
        <div className="uf-ribbon">
          <div className="uf-ribbon-left">
            <span className="uf-ribbon-eyebrow"><Layers size={13} /> {t.userForms.chooseMoldLabel}</span>
            <div className="uf-ribbon-title">
              <h2>{t.userForms.chooseMoldTitle}</h2>
              <p>{t.userForms.chooseMoldSub(moldTypes.length)}</p>
            </div>
          </div>
        </div>

        <div className="uf-picker uf-mold-picker">
          {moldTypes.map((mold) => (
            <button
              type="button"
              key={mold.id}
              className="uf-picker-card uf-mold-card"
              onClick={() => setSelectedMoldId(mold.id)}
            >
              <span className="uf-mold-thumb">
                {mold.imageUrl ? <img src={mold.imageUrl} alt={mold.name} /> : <ImageIcon size={24} />}
              </span>
              <span className="uf-picker-body">
                <strong>{mold.name}</strong>
              </span>
            </button>
          ))}
        </div>
      </section>
    );
  }

  // selectedMoldId set edildi ama slimMold henüz resolve olmadı (ilk render gecikmesi)
  if (!slimMold) return null;

  return (
    <section className="uf-stage">
      {/* Kurumsal üst şerit — başlık yüklenen forma göre, geri dönüş kalıp listesine */}
      <div className="uf-ribbon">
        <div className="uf-ribbon-left">
          {moldTypes.length > 1 && (
            <button
              type="button"
              className="uf-back-btn"
              onClick={() => setSelectedMoldId(null)}
              title={t.userForms.moldList}
            >
              <ArrowLeft size={14} /> {t.userForms.moldList}
            </button>
          )}
          <span className="uf-ribbon-eyebrow"><Layers size={13} /> {t.userForms.selectedMoldLabel}</span>
          <div className="uf-ribbon-title">
            <h2>{fullForm?.name || slimMold.name}</h2>
            {fullForm?.description && <p>{fullForm.description}</p>}
          </div>
        </div>
      </div>

      {/* Form sahnesi — yüklenirken spinner, hazır olunca renderer */}
      <div className="uf-canvas">
        <div className="uf-canvas-inner">
          {loadingForm || !fullForm ? (
            <div className="uf-loading">
              <Loader2 className="uf-loading-icon" size={32} />
              <span>{t.userForms.loadingForm}</span>
            </div>
          ) : (
            <DynamicFormRenderer form={fullForm} onSubmit={handleSubmit} />
          )}
        </div>
        <footer className="uf-footnote">
          <span><ShieldCheck size={13} /> {t.userForms.footerSecure}</span>
          <span>{t.userForms.footerPlatform}</span>
        </footer>
      </div>
    </section>
  );
}
